import { format } from 'date-fns';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { ProductCreate } from '../../Inventory/Product';
import { EstimateAndQuoteCreateCustomerSelectEdit } from '../EstimateAndQuote/EstimateAndQuoteCreateCustomerSelectEdit';

import { Box, BoxColumn, BoxRowColumn } from '@components/Box';
import { CollapsibleBoxColumn } from '@components/CollapsibleBoxColumn';
import { FormSectionContainer } from '@components/Container';
import { CustomerDisplayCard } from '@components/CustomerDisplayCard';
import { ExpandingBoxColumn } from '@components/ExpandingBoxColumn';
import { FormikInput, FormikSelect, FormikTextArea } from '@components/Form';
import { FormikDateInput } from '@components/Form/FormikDateInput';
import { FormActionsBar } from '@components/FormActionsBar';
import { LineItemsTable } from '@components/LineItemsTable';
import { Loading } from '@components/Loading';
import { Modal } from '@components/Modal';
import { routes } from '@config/routes';
import type { SubmitType } from '@hooks/useFormSubmission';
import { useLineItems } from '@hooks/useLineItems';
import { useNotification } from '@hooks/useNotification';
import { useSalesTaxEffectiveRates } from '@hooks/useSalesTaxEffectiveRates';
import { FormikSelectOption } from '@models/common/FormikSelectOption';
import { CustomerViewModel } from '@models/Customer';
import {
  InvoiceViewModel,
  invoiceDefaultValue,
  invoiceValidation,
} from '@models/Invoice';
import {
  InvoiceDetailViewModel,
  invoiceDetailDefaultValue,
} from '@models/InvoiceDetail';
import {
  InvoiceDetailSalesTaxViewModel,
  invoiceDetailSalesTaxDefaultValue,
} from '@models/InvoiceDetailSalesTax';
import { ContactService } from '@services/Contact';
import { CustomerService } from '@services/Customer';
import { InvoiceService } from '@services/Invoice';
import { ProductService } from '@services/Product';

export const InvoiceEdit = () => {
  const { showInfo, showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation = location.state?.from || routes.RNC_INVOICES;
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  const [isInvoiceLoading, setInvoiceIsLoading] = useState(true);
  const [invoice, setInvoice] = useState<InvoiceViewModel>(invoiceDefaultValue);

  const [isInvoiceDetailsLoading, setInvoiceDetailsIsLoading] = useState(true);

  // Line items hook - manages invoice details
  const {
    lineItems: invoiceDetails,
    setLineItems: setInvoiceDetails,
    addLineItem: handleAddInvoiceDetail,
    removeLineItem: handleRemoveInvoiceDetail,
    resetLineItem: handleResetInvoiceDetail,
    updateLineItemQuantity,
    updateLineItemSalesTax,
    updateSalesTaxRates,
    recalculateTaxAmounts,
    getSalesTaxesBySalesTaxID,
  } = useLineItems<InvoiceDetailViewModel, InvoiceDetailSalesTaxViewModel>(
    {
      defaultLineItem: invoiceDetailDefaultValue,
      defaultSalesTax: invoiceDetailSalesTaxDefaultValue,
      salesTaxesKey: 'invoiceDetailSalesTaxes',
    },
    { onInfo: showInfo }
  );

  const [isCustomerContactDetailsLoading, setCustomerContactDetailsIsLoading] =
    useState<boolean>(true);
  const [isCustomerContactsLoading, setCustomerContactsIsLoading] =
    useState<boolean>(true);
  const [customerContacts, setCustomerContacts] = useState<
    FormikSelectOption[]
  >([]);

  const [
    isCustomerBillingAddressesLoading,
    setCustomerBillingAddressesIsLoading,
  ] = useState<boolean>(true);
  const [customerBillingAddresses, setCustomerBillingAddresses] = useState<
    FormikSelectOption[]
  >([]);
  const [submitType, setSubmitType] = useState<SubmitType>('saveAndClose');

  const [
    isCustomerShippingAddressesLoading,
    setCustomerShippingAddressesIsLoading,
  ] = useState<boolean>(true);
  const [customerShippingAddresses, setCustomerShippingAddresses] = useState<
    FormikSelectOption[]
  >([]);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerViewModel | null>(null);

  const {
    salesTaxes,
    salesTaxEffectiveRates,
    isLoading: isSalesTaxEffectiveRatesLoading,
    loadData: loadSalesTaxEffectiveRates,
  } = useSalesTaxEffectiveRates();

  const [isProductsLoading, setProductsIsLoading] = useState(true);
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [products, setProducts] = useState<FormikSelectOption[]>([]);

  const [isLeftColumnCollapsed, setIsLeftColumnCollapsed] = useState(false);

  const loadInvoice = () => {
    InvoiceService.getById(Number(id)).then((result) => {
      if (result.isSuccess && 'data' in result) {
        setInvoice(result.data);
        setInvoiceIsLoading(false);
        const successMessage = `Successfully fetched the invoice record.`;
        console.log(successMessage);
      } else {
        const errorMessage = `Failed to get the invoice record. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  const loadInvoiceCustomerContacts = (customerId: number) => {
    if (customerId === 0) {
      setCustomerContacts([{ value: '0', text: '' }]);
      setCustomerContactsIsLoading(false);
    } else {
      CustomerService.getCustomerContactsSelectOptions(customerId).then(
        (result) => {
          if ('data' in result) {
            const customerContactsSelectOptions = [
              { value: '0', text: '' },
            ].concat(
              result.data.map((row) => ({
                value: row.value,
                text: row.text,
              }))
            );

            setCustomerContacts(customerContactsSelectOptions);
            setCustomerContactsIsLoading(false);
          } else {
            const errorMessage = `Failed to get customer contacts select options. ${result.errorMessages}`;
            console.error(errorMessage);
            showError(errorMessage);
          }
        }
      );
    }
  };

  const loadInvoiceCustomerContactDetails = (contactId: number) => {
    if (contactId === 0) {
      setInvoice({
        ...invoice,
        contactEmail: '',
        contactPhone: '',
      });
      setCustomerContactDetailsIsLoading(false);
    } else {
      ContactService.getById(contactId).then((result) => {
        if (result.isSuccess && 'data' in result) {
          setInvoice({
            ...invoice,
            contactEmail: result.data.email ?? '',
            contactPhone: result.data.phone ?? '',
          });
          setCustomerContactDetailsIsLoading(false);
        } else {
          const errorMessage = `Failed to get the selected contact details. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadInvoiceCustomerBillingAddresses = (customerId: number) => {
    if (customerId === 0) {
      setCustomerBillingAddresses([{ value: '0', text: '' }]);
      setCustomerBillingAddressesIsLoading(false);
    } else {
      CustomerService.getCustomerBillingAddressesSelectOptions(customerId).then(
        (result) => {
          if ('data' in result) {
            const customerBillingAddressesSelectOptions = [
              { value: '0', text: '' },
            ].concat(
              result.data.map((row) => ({
                value: row.value,
                text: row.text,
              }))
            );

            setCustomerBillingAddresses(customerBillingAddressesSelectOptions);
            setCustomerBillingAddressesIsLoading(false);
          } else {
            const errorMessage = `Failed to get customer billing addresses select options. ${result.errorMessages}`;
            console.error(errorMessage);
            showError(errorMessage);
          }
        }
      );
    }
  };

  const loadInvoiceCustomerShippingAddresses = (customerId: number) => {
    if (customerId === 0) {
      setCustomerShippingAddresses([{ value: '0', text: '' }]);
      setCustomerShippingAddressesIsLoading(false);
    } else {
      CustomerService.getCustomerShippingAddressesSelectOptions(
        customerId
      ).then((result) => {
        if ('data' in result) {
          const customerShippingAddressesSelectOptions = [
            { value: '0', text: '' },
          ].concat(
            result.data.map((row) => ({
              value: row.value,
              text: row.text,
            }))
          );

          setCustomerShippingAddresses(customerShippingAddressesSelectOptions);
          setCustomerShippingAddressesIsLoading(false);
        } else {
          const errorMessage = `Failed to get customer shipping addresses select options. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadInvoiceDetails = (invoiceId: number) => {
    if (invoiceId === 0) {
      setInvoiceDetails([]);
    } else {
      InvoiceService.getInvoiceDetails(Number(id)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          const invoiceDetails = result.data.map((row) => ({
            ...row,
            invoiceDetailSalesTaxes: row.invoiceDetailSalesTaxes.concat([
              invoiceDetailSalesTaxDefaultValue,
            ]),
          }));
          setInvoiceDetails(invoiceDetails);
          setInvoiceDetailsIsLoading(false);
        } else {
          const errorMessage = `Failed to get the invoice details. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadSelectedCustomer = (customerId: string, formikProps: any) => {
    if (customerId === '0') {
      setSelectedCustomer(null);
      // Clear tax exemption when customer is cleared
      formikProps.setFieldValue('isTaxExempt', false);
      // Update the invoice state to keep it in sync
      setInvoice((prev) => ({ ...prev, customerID: 0, isTaxExempt: false }));
      // Recalculate tax amounts when customer is cleared (non-exempt)
      setTimeout(() => recalculateTaxAmountsForCustomer(false), 100);
      formikProps.setFieldValue('contactID', 0);
      formikProps.setFieldValue('contactEmail', '');
      formikProps.setFieldValue('contactPhone', '');
      formikProps.setFieldValue('billingAddressID', 0);
      formikProps.setFieldValue('isSameShipping', false);
      formikProps.setFieldValue('shippingAddressID', 0);
      setCustomerContacts([{ value: '0', text: '' }]);
      setCustomerBillingAddresses([{ value: '0', text: '' }]);
      setCustomerShippingAddresses([{ value: '0', text: '' }]);
    } else {
      CustomerService.getById(Number(customerId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          setSelectedCustomer(result.data);
          // When customer changes, use the customer's tax exemption status
          formikProps.setFieldValue('isTaxExempt', result.data.isTaxExempt);
          // Update the invoice state to keep it in sync
          setInvoice((prev) => ({
            ...prev,
            customerID: result.data.id,
            isTaxExempt: result.data.isTaxExempt || false,
          }));
          // Recalculate tax amounts based on the customer's tax exemption status
          setTimeout(
            () =>
              recalculateTaxAmountsForCustomer(
                result.data.isTaxExempt || false
              ),
            100
          );
          formikProps.setFieldValue(
            'contactID',
            result.data.primaryContact?.id || 0
          );
          formikProps.setFieldValue(
            'contactEmail',
            result.data.primaryContact?.email || ''
          );
          formikProps.setFieldValue(
            'contactPhone',
            result.data.primaryContact?.phone || ''
          );
          formikProps.setFieldValue(
            'billingAddressID',
            result.data.billingID || 0
          );
          formikProps.setFieldValue(
            'isSameShipping',
            result.data.isSameShipping
          );
          formikProps.setFieldValue(
            'shippingAddressID',
            result.data.shippingID || 0
          );

          // Recalculate taxes based on new customer's tax exemption status
          setTimeout(() => {
            recalculateTaxAmountsForCustomer(result.data.isTaxExempt || false);
          }, 100);
        } else {
          const errorMessage = `Failed to get the selected customer record. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });

      CustomerService.getCustomerContactsSelectOptions(Number(customerId)).then(
        (result) => {
          if ('data' in result) {
            const customerContactsSelectOptions = [
              { value: '0', text: '' },
            ].concat(
              result.data.map((row) => ({
                value: row.value,
                text: row.text,
              }))
            );

            setCustomerContacts(customerContactsSelectOptions);
          } else {
            const errorMessage = `Failed to get customer contacts select options. ${result.errorMessages}`;
            console.error(errorMessage);
            showError(errorMessage);
          }
        }
      );

      CustomerService.getCustomerBillingAddressesSelectOptions(
        Number(customerId)
      ).then((result) => {
        if ('data' in result) {
          const customerBillingAddressesSelectOptions = [
            { value: '0', text: '' },
          ].concat(
            result.data.map((row) => ({
              value: row.value,
              text: row.text,
            }))
          );

          setCustomerBillingAddresses(customerBillingAddressesSelectOptions);
        } else {
          const errorMessage = `Failed to get customer billing addresses select options. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });

      CustomerService.getCustomerShippingAddressesSelectOptions(
        Number(customerId)
      ).then((result) => {
        if ('data' in result) {
          const customerShippingAddressesSelectOptions = [
            { value: '0', text: '' },
          ].concat(
            result.data.map((row) => ({
              value: row.value,
              text: row.text,
            }))
          );

          setCustomerShippingAddresses(customerShippingAddressesSelectOptions);
        } else {
          const errorMessage = `Failed to get customer shipping addresses select options. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadSelectedContact = (contactId: string, formikProps: any) => {
    if (contactId === '0') {
      formikProps.setFieldValue('contactEmail', '');
      formikProps.setFieldValue('contactPhone', '');
    } else {
      ContactService.getById(Number(contactId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          formikProps.setFieldValue('contactEmail', result.data.email || '');
          formikProps.setFieldValue('contactPhone', result.data.phone || '');
        } else {
          const errorMessage = `Failed to get the selected contact details. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  const loadProducts = () => {
    ProductService.getProductSelectOptions().then((result) => {
      if ('data' in result) {
        const productsSelectOptions = [{ value: '0', text: '' }].concat(
          result.data.map((row) => ({
            value: row.value,
            text: row.text,
          }))
        );
        setProducts(productsSelectOptions);
        setProductsIsLoading(false);
      } else {
        const errorMessage = `Failed to get the product select options. ${result.errorMessages}`;
        console.error(errorMessage);
        showError(errorMessage);
      }
    });
  };

  // Recalculate tax amounts using the hook
  const recalculateTaxAmountsForCustomer = (isTaxExempt: boolean) => {
    recalculateTaxAmounts(isTaxExempt);
  };

  const loadSelectedProduct = (
    productId: string,
    index: number,
    isTaxExempt?: boolean
  ) => {
    const taxExemptStatus =
      isTaxExempt !== undefined ? isTaxExempt : invoice.isTaxExempt;

    const emptyInvoiceDetail: InvoiceDetailViewModel =
      invoiceDetailDefaultValue;
    if (productId === '0') {
      const updatedInvoiceDetails = [...invoiceDetails];
      updatedInvoiceDetails[index] = emptyInvoiceDetail;
      setInvoiceDetails(updatedInvoiceDetails);
    } else {
      ProductService.getById(Number(productId)).then((result) => {
        if (result.isSuccess && 'data' in result) {
          const invoiceDetailSalesTaxes = result.data.productSalesTaxes
            .map((o) => {
              const salesTaxEffectiveRate = salesTaxEffectiveRates.find(
                (i) => i.id == o.salesTaxId
              );
              return {
                salesTaxID: o.salesTaxId,
                salesTaxAbbreviation: o.salesTax.abbreviation,
                rate: salesTaxEffectiveRate ? salesTaxEffectiveRate.rate : 0,
                salesTaxAmount:
                  taxExemptStatus === true
                    ? 0
                    : (Number(
                        salesTaxEffectiveRate ? salesTaxEffectiveRate.rate : 0
                      ) *
                        Number(result.data.price)) /
                      100,
              } as InvoiceDetailSalesTaxViewModel;
            })
            .concat([invoiceDetailSalesTaxDefaultValue]);

          const newInvoiceDetail: InvoiceDetailViewModel = {
            id: 0,
            invoiceID: 0,
            productID: result.data.id,
            product: result.data,
            description: result.data.description,
            quantity: 1,
            price: result.data.price,
            invoiceDetailSalesTaxes: invoiceDetailSalesTaxes,
            subTotal: result.data.price,
          };

          const updatedInvoiceDetails = [...invoiceDetails];
          updatedInvoiceDetails[index] = newInvoiceDetail;
          setInvoiceDetails(updatedInvoiceDetails);
        } else {
          const errorMessage = `Failed to get the selected product details. ${result.errorMessages}`;
          console.error(errorMessage);
          showError(errorMessage);
        }
      });
    }
  };

  // Wrapper to adapt hook's updateLineItemQuantity to existing signature
  const handleUpdateInvoiceDetailQuantity = (
    index: number,
    quantity: number,
    isTaxExempt?: boolean
  ) => {
    const taxExemptStatus =
      isTaxExempt !== undefined ? isTaxExempt : invoice.isTaxExempt;
    updateLineItemQuantity(index, quantity, taxExemptStatus);
  };

  // Handle updating sales tax on a line item
  const handleUpdateInvoiceDetailSalesTax = (
    invoiceDetailIndex: number,
    invoiceDetailSalesTaxIndex: number,
    _invoiceDetailSalesTaxesLength: number,
    salesTaxID: number,
    isTaxExempt?: boolean
  ) => {
    const taxExemptStatus =
      isTaxExempt !== undefined ? isTaxExempt : invoice.isTaxExempt;
    const salesTax = salesTaxEffectiveRates.find((o) => o.id == salesTaxID);
    updateLineItemSalesTax(
      invoiceDetailIndex,
      invoiceDetailSalesTaxIndex,
      salesTax,
      taxExemptStatus
    );
  };

  // Use hook's updateSalesTaxRates for updating tax rates
  const handleUpdateInvoiceDetailSalesTaxRates = (isTaxExempt?: boolean) => {
    const taxExemptStatus =
      isTaxExempt !== undefined ? isTaxExempt : invoice.isTaxExempt;
    updateSalesTaxRates(salesTaxEffectiveRates, taxExemptStatus);
  };

  useEffect(() => {
    handleUpdateInvoiceDetailSalesTaxRates(invoice.isTaxExempt);
  }, [salesTaxEffectiveRates]);

  useEffect(() => {
    if (!isInvoiceLoading) {
      // Load selected customer data for tax status display
      if (invoice.customerID && invoice.customerID !== 0) {
        CustomerService.getById(Number(invoice.customerID)).then((result) => {
          if (result.isSuccess && 'data' in result) {
            setSelectedCustomer(result.data);
            // Recalculate tax amounts based on the loaded customer's tax exemption status
            setTimeout(
              () =>
                recalculateTaxAmountsForCustomer(
                  result.data.isTaxExempt || false
                ),
              100
            );
          }
        });
      }

      loadInvoiceCustomerContacts(Number(invoice.customerID));
      loadInvoiceCustomerContactDetails(Number(invoice.contactID));
      loadInvoiceCustomerBillingAddresses(Number(invoice.customerID));
      loadInvoiceCustomerShippingAddresses(Number(invoice.customerID));
      loadSalesTaxEffectiveRates(new Date(invoice.date));
    }
  }, [isInvoiceLoading]);

  // Recalculate taxes when invoice details finish loading and we have a selected customer
  useEffect(() => {
    if (
      !isInvoiceDetailsLoading &&
      selectedCustomer &&
      invoiceDetails.length > 0
    ) {
      setTimeout(
        () =>
          recalculateTaxAmountsForCustomer(
            selectedCustomer.isTaxExempt || false
          ),
        100
      );
    }
  }, [isInvoiceDetailsLoading, selectedCustomer]);

  useEffect(() => {
    if (
      !isSalesTaxEffectiveRatesLoading &&
      !isInvoiceLoading &&
      !isInvoiceDetailsLoading &&
      !isCustomerContactDetailsLoading &&
      !isCustomerContactsLoading &&
      !isCustomerBillingAddressesLoading &&
      !isCustomerShippingAddressesLoading &&
      !isProductsLoading
    ) {
      setIsLoading(false);
    }
  }, [
    isSalesTaxEffectiveRatesLoading,
    isInvoiceLoading,
    isInvoiceDetailsLoading,
    isCustomerContactDetailsLoading,
    isCustomerContactsLoading,
    isCustomerBillingAddressesLoading,
    isCustomerShippingAddressesLoading,
    isProductsLoading,
  ]);

  useEffect(() => {
    if (id) {
      loadInvoice();
      loadInvoiceDetails(Number(id));
      loadProducts();
    }
  }, []);

  if (isLoading) {
    return (
      <>
        <Loading />
      </>
    );
  } else {
    return (
      <>
        <Box>
          <div className="pt-2 lg:pb-6 md:pb-4 pb-2">
            <p className="w-full text-center lg:text-2xl md:text-lg text-md font-bold text-primary-900">
              Edit Invoice Details
            </p>
          </div>
          <Formik
            initialValues={invoice}
            validationSchema={invoiceValidation}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={async (values, actions) => {
              const formattedValues = {
                ...values,
                date: format(new Date(values.date), 'yyyy-MM-dd'),
                dueDate: format(new Date(values.dueDate), 'yyyy-MM-dd'),
                discountType: Number(values.discountType),
              };

              formattedValues.invoiceDetails = invoiceDetails;

              try {
                InvoiceService.update(Number(id), formattedValues)
                  .then((result) => {
                    if (result.isSuccess) {
                      const successMessage = `Successfully updated the invoice record.`;
                      showSuccess(successMessage);

                      if (submitType === 'saveAndClose') {
                        navigate(`${routes.RNC_INVOICES}/${id}`);
                      } else if (submitType === 'save') {
                        window.location.reload();
                      }
                    } else {
                      const errorMessage = `Failed to update the invoice record. ${result.errorMessages}`;
                      console.error(errorMessage);
                      showError(errorMessage);
                    }
                  })
                  .finally(() => {
                    actions.setSubmitting(false);
                  });
              } catch (error) {
                console.error(error);
                actions.setSubmitting(false);
              }
            }}
          >
            {(formikProps) => {
              return (
                <>
                  <form method="POST" onSubmit={formikProps.handleSubmit}>
                    <FormSectionContainer>
                      {/* Calling Card - Only visible when left column is collapsed */}
                      {isLeftColumnCollapsed && selectedCustomer && (
                        <CustomerDisplayCard
                          selectedCustomer={selectedCustomer}
                          contactID={formikProps.values.contactID}
                          contactEmail={formikProps.values.contactEmail}
                          customerContacts={customerContacts}
                          onEditClick={() => setIsLeftColumnCollapsed(false)}
                        />
                      )}

                      <BoxRowColumn>
                        <CollapsibleBoxColumn
                          isCollapsed={isLeftColumnCollapsed}
                          onToggleCollapse={() =>
                            setIsLeftColumnCollapsed(!isLeftColumnCollapsed)
                          }
                          canCollapse={!!selectedCustomer}
                          collapseTitle="Collapse form"
                          disabledTitle="Please select a customer first"
                          className="w-full lg:w-[73%] md:w-full transition-all duration-300"
                        >
                          <FormikInput
                            label="Invoice Number"
                            name="invoiceNumber"
                            readonly={true}
                          />

                          <EstimateAndQuoteCreateCustomerSelectEdit
                            initialSelectedCustomerId={`${invoice.customerID}`}
                            onCustomerSelectChange={(customerId: string) => {
                              loadSelectedCustomer(customerId, formikProps);
                            }}
                          />

                          {selectedCustomer && (
                            <div className="mb-4">
                              <label className="flex items-center space-x-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={
                                    formikProps.values.isTaxExempt || false
                                  }
                                  onChange={(e) => {
                                    formikProps.setFieldValue(
                                      'isTaxExempt',
                                      e.target.checked
                                    );
                                    // Update the invoice state to keep it in sync
                                    setInvoice((prev) => ({
                                      ...prev,
                                      isTaxExempt: e.target.checked,
                                    }));
                                    // Recalculate tax amounts when tax exemption status changes
                                    setTimeout(() => {
                                      recalculateTaxAmountsForCustomer(
                                        e.target.checked
                                      );
                                    }, 0);
                                  }}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="font-medium text-gray-700">
                                  Tax Exempt
                                </span>
                              </label>
                            </div>
                          )}

                          <FormikInput
                            label="Customer Ref"
                            name="customerRef"
                          />
                          <FormikSelect
                            label="Contact Name"
                            name="contactID"
                            selection={customerContacts}
                            onChange={(
                              event: React.ChangeEvent<HTMLSelectElement>
                            ) => {
                              loadSelectedContact(
                                event.target.value,
                                formikProps
                              );
                            }}
                          />
                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikInput
                                label="Email"
                                name="contactEmail"
                                readonly={true}
                              />
                            </BoxColumn>

                            <BoxColumn>
                              <FormikInput
                                label="Phone"
                                name="contactPhone"
                                readonly={true}
                              />
                            </BoxColumn>
                          </BoxRowColumn>
                          <BoxRowColumn>
                            <BoxColumn>
                              <FormikDateInput
                                label="Date (mm/dd/yyyy)"
                                name="date"
                                type="date"
                                changeHandler={(value: string) => {
                                  loadSalesTaxEffectiveRates(new Date(value));
                                }}
                              />
                            </BoxColumn>

                            <BoxColumn>
                              <FormikDateInput
                                label="Due Date (mm/dd/yyyy)"
                                name="dueDate"
                                type="date"
                                onChange={(e: any) =>
                                  formikProps.setFieldValue(
                                    'dueDate',
                                    format(
                                      new Date(e.target.value),
                                      'MM/dd/yyyy'
                                    )
                                  )
                                }
                              />
                            </BoxColumn>
                          </BoxRowColumn>
                          <FormikSelect
                            label="Billing Address"
                            name="billingAddressID"
                            selection={customerBillingAddresses}
                          />
                          {!formikProps.values.isSameShipping ? (
                            <FormikSelect
                              label="Shipping Address"
                              name="shippingAddressID"
                              selection={customerShippingAddresses}
                            />
                          ) : (
                            <>
                              <label className="block lg:text-sm md:text-sm text-xs font-medium text-primary-700">
                                Shipping Address
                              </label>
                              <i>Same as Billing Address</i>
                            </>
                          )}
                          <FormikTextArea label="Notes" name="notes" />
                        </CollapsibleBoxColumn>

                        <ExpandingBoxColumn isExpanded={isLeftColumnCollapsed}>
                          <div className="py-4">
                            <LineItemsTable<
                              InvoiceDetailViewModel,
                              InvoiceDetailSalesTaxViewModel
                            >
                              lineItems={invoiceDetails}
                              products={products}
                              salesTaxes={salesTaxes || []}
                              salesTaxesKey="invoiceDetailSalesTaxes"
                              onProductSelect={(productId, index) =>
                                loadSelectedProduct(
                                  productId,
                                  index,
                                  formikProps.values.isTaxExempt
                                )
                              }
                              onQuantityChange={
                                handleUpdateInvoiceDetailQuantity
                              }
                              onSalesTaxChange={
                                handleUpdateInvoiceDetailSalesTax
                              }
                              onAddLineItem={handleAddInvoiceDetail}
                              onRemoveLineItem={handleRemoveInvoiceDetail}
                              onCreateProductClick={() =>
                                setShowCreateProduct(true)
                              }
                              getSalesTaxesBySalesTaxID={() =>
                                getSalesTaxesBySalesTaxID
                              }
                              formikValues={{
                                isTaxExempt: formikProps.values.isTaxExempt,
                                discount: formikProps.values.discount,
                                discountType: formikProps.values.discountType,
                              }}
                            />
                          </div>

                          <div className="flex items-end justify-end h-full">
                            <FormActionsBar
                              onCancel={() => navigate(previousLocation)}
                              isSubmitting={formikProps.isSubmitting}
                              isValid={formikProps.isValid}
                              buttons={[
                                {
                                  label: 'Save & Close',
                                  submitType: 'saveAndClose',
                                },
                                { label: 'Save', submitType: 'save' },
                              ]}
                              onSubmitTypeChange={setSubmitType}
                            />
                          </div>
                        </ExpandingBoxColumn>
                      </BoxRowColumn>
                    </FormSectionContainer>
                  </form>
                </>
              );
            }}
          </Formik>
        </Box>
        {showCreateProduct && (
          <>
            <Modal show={true}>
              <ProductCreate
                onClose={(productResultViewModel) => {
                  setShowCreateProduct(false);

                  if (productResultViewModel && productResultViewModel.data) {
                    const newProducts = [
                      ...products.slice(0, products.length - 1),
                      {
                        value: productResultViewModel.data.id.toString(),
                        text: productResultViewModel.data.name,
                      },
                    ];

                    setProducts(newProducts);

                    loadSelectedProduct(
                      productResultViewModel.data.id.toString(),
                      invoiceDetails.length - 1
                    );
                  } else {
                    handleResetInvoiceDetail(invoiceDetails.length - 1);
                  }
                }}
              />
            </Modal>
          </>
        )}
      </>
    );
  }
};
